/**
 * File signature validation utility
 * Validates file content matches its extension using magic bytes
 */

/** Dropzone `accept` map for capsule audio uploads. MIME aliases cover OS/browser quirks. */
export const AUDIO_FILE_ACCEPT: { [key: string]: readonly string[] } = {
    "audio/mpeg": [".mp3"],
    "audio/mp3": [".mp3"],
    "audio/wav": [".wav"],
    "audio/x-wav": [".wav"],
    "audio/wave": [".wav"],
    "audio/x-m4a": [".m4a"],
    "audio/mp4": [".m4a"],
    "audio/aac": [".m4a"],
    "audio/m4a": [".m4a"],
};

const ACCEPTED_AUDIO_EXTENSIONS = new Set([".mp3", ".wav", ".m4a"]);

/** True when the file name uses an audio extension we actually persist on a capsule. */
export const isAcceptedAudioFile = (file: File): boolean => {
    const name = (file.name || "").toLowerCase();
    const dot = name.lastIndexOf(".");
    if (dot === -1) return false;
    return ACCEPTED_AUDIO_EXTENSIONS.has(name.slice(dot));
};

/**
 * Read the first N bytes of a file as an ArrayBuffer
 */
const readFileHeader = async (file: File, bytes: number = 16): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            if (e.target?.result instanceof ArrayBuffer) {
                resolve(e.target.result);
            } else {
                reject(new Error("Failed to read file header"));
            }
        };
        reader.onerror = () => reject(new Error("Error reading file"));
        reader.readAsArrayBuffer(file.slice(0, bytes));
    });
};

/**
 * Convert ArrayBuffer to hex string
 */
const bufferToHex = (buffer: ArrayBuffer): string => {
    return Array.from(new Uint8Array(buffer))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
};

/**
 * Convert ArrayBuffer to ASCII string
 */
const bufferToAscii = (buffer: ArrayBuffer): string => {
    return Array.from(new Uint8Array(buffer))
        .map((b) => String.fromCharCode(b))
        .join("");
};

/**
 * Check if buffer contains null bytes (indicates binary content)
 */
const hasNullBytes = (buffer: ArrayBuffer): boolean => {
    const bytes = new Uint8Array(buffer);
    return bytes.some((b) => b === 0x00);
};

/**
 * Validate file signature matches extension
 * @param file - File object to validate
 * @returns Object with isValid flag and error code if invalid
 */
export const validateFileSignature = async (file: File): Promise<{ isValid: boolean; errorCode?: string }> => {
    try {
        const fileName = file.name.toLowerCase();
        const extension = fileName.substring(fileName.lastIndexOf("."));

        // Read first 16 bytes for signature checking
        const header = await readFileHeader(file, 16);
        const hex = bufferToHex(header);
        const magic4 = hex.substring(0, 8); // First 4 bytes as hex
        const ascii = bufferToAscii(header);

        // PDF validation
        if (extension === ".pdf") {
            if (magic4 !== "25504446") {
                // %PDF
                return {
                    isValid: false,
                    errorCode: "FILE_SIGNATURE_MISMATCH_PDF",
                };
            }
        }

        // DOCX validation (ZIP format)
        if (extension === ".docx") {
            if (magic4 !== "504b0304") {
                // ZIP signature
                return {
                    isValid: false,
                    errorCode: "FILE_SIGNATURE_MISMATCH_DOCX",
                };
            }
        }

        // JPEG validation
        if (extension === ".jpg" || extension === ".jpeg") {
            if (hex.substring(0, 4) !== "ffd8") {
                // JPEG signature
                return {
                    isValid: false,
                    errorCode: "FILE_SIGNATURE_MISMATCH_JPEG",
                };
            }
        }

        // PNG validation
        if (extension === ".png") {
            if (hex.substring(0, 16) !== "89504e470d0a1a0a") {
                // PNG signature
                return {
                    isValid: false,
                    errorCode: "FILE_SIGNATURE_MISMATCH_PNG",
                };
            }
        }

        // WEBP validation
        if (extension === ".webp") {
            const riff = ascii.substring(0, 4);
            const webp = ascii.substring(8, 12);
            if (riff !== "RIFF" || webp !== "WEBP") {
                return {
                    isValid: false,
                    errorCode: "FILE_SIGNATURE_MISMATCH_WEBP",
                };
            }
        }

        // XLS validation (OLE2 format)
        if (extension === ".xls") {
            if (magic4 !== "d0cf11e0") {
                return {
                    isValid: false,
                    errorCode: "FILE_SIGNATURE_MISMATCH_XLS",
                };
            }
        }

        // XLSX validation (Office Open XML/ZIP)
        if (extension === ".xlsx") {
            if (magic4 !== "504b0304") {
                // ZIP signature
                return {
                    isValid: false,
                    errorCode: "FILE_SIGNATURE_MISMATCH_XLSX",
                };
            }
        }

        // HEIC validation
        if (extension === ".heic") {
            const ftyp = ascii.substring(4, 8);
            const brand = ascii.substring(8, 12);
            if (ftyp !== "ftyp" || (brand !== "heic" && brand !== "mif1" && brand !== "msf1")) {
                return {
                    isValid: false,
                    errorCode: "FILE_SIGNATURE_MISMATCH_HEIC",
                };
            }
        }

        // CSV validation - check it's text-based and has CSV patterns
        if (extension === ".csv") {
            // Read more bytes for CSV validation (first 512 bytes)
            const csvHeader = await readFileHeader(file, Math.min(512, file.size));
            if (hasNullBytes(csvHeader)) {
                return {
                    isValid: false,
                    errorCode: "FILE_SIGNATURE_MISMATCH_CSV",
                };
            }
            const csvText = bufferToAscii(csvHeader);
            const hasCSVPattern = /[,;\t]/.test(csvText) || csvText.includes("\n");
            if (!hasCSVPattern) {
                return {
                    isValid: false,
                    errorCode: "FILE_SIGNATURE_MISMATCH_CSV",
                };
            }
        }

        // TXT validation - check it's text-based (not binary)
        if (extension === ".txt") {
            // Read more bytes for TXT validation (first 512 bytes)
            const txtHeader = await readFileHeader(file, Math.min(512, file.size));
            if (hasNullBytes(txtHeader)) {
                return {
                    isValid: false,
                    errorCode: "FILE_SIGNATURE_MISMATCH_TXT",
                };
            }
        }

        return { isValid: true };
    } catch (error) {
        console.error("Error validating file signature:", error);
        // If validation fails due to error, allow the file through
        // Backend validation will catch it
        return { isValid: true };
    }
};

/**
 * Validate multiple files
 */
export const validateFilesSignatures = async (
    files: File[],
): Promise<{ validFiles: File[]; invalidFiles: { file: File; errorCode: string }[] }> => {
    const validFiles: File[] = [];
    const invalidFiles: { file: File; errorCode: string }[] = [];

    for (const file of files) {
        const validation = await validateFileSignature(file);
        if (validation.isValid) {
            validFiles.push(file);
        } else if (validation.errorCode) {
            invalidFiles.push({ file, errorCode: validation.errorCode });
        }
    }

    return { validFiles, invalidFiles };
};
